// The canvas's view (spec capcut-shortcuts.md): a tool, a zoom and a pan, the tab's own and never
// the project's. Pure: the arithmetic the preview and its tools share.

/** Move is today's canvas; the Hand tool's drag pans the view instead. */
export type CanvasTool = "move" | "hand";

/** "fit", or a scale of the frame's own pixels: 1 is one CSS pixel a frame pixel (100 %). */
export type CanvasZoom = "fit" | number;

export interface CanvasView {
  tool: CanvasTool;
  zoom: CanvasZoom;
  /** The stage's offset from its fitted place, in CSS pixels. */
  pan: { x: number; y: number };
}

/** How a project opens: fitted, with Move. */
export const FIT_VIEW: CanvasView = { tool: "move", zoom: "fit", pan: { x: 0, y: 0 } };

/** ⇧+ and ⇧−'s stops, as scales of the frame's own pixels: 10 % to 400 %. */
export const ZOOM_STEPS: readonly number[] = [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4];

/** The frame's scale at the fit: the stage's width over the frame's; 0 before the stage is laid out. */
export function fitScale(stageWidth: number, frameWidth: number): number {
  return stageWidth > 0 && frameWidth > 0 ? stageWidth / frameWidth : 0;
}

/** How much the stage is scaled by CSS over its fitted size. */
export function stageScale(zoom: CanvasZoom, fit: number): number {
  return zoom === "fit" || fit <= 0 ? 1 : zoom / fit;
}

/** The zoom as the tools write it: "Fit", or its percentage. */
export function zoomLabel(zoom: CanvasZoom): string {
  return zoom === "fit" ? "Fit" : `${Math.round(zoom * 100)}%`;
}

/** The next stop up (1) or down (-1) from where the zoom is, held to the first and the last. */
export function steppedZoom(zoom: CanvasZoom, fit: number, by: 1 | -1): number {
  const now = zoom === "fit" ? fit : zoom;
  // A stop the zoom is already at (within a rounding) is not a step.
  const apart = (step: number) => Math.abs(step - now) > 1e-6;
  if (by > 0) return ZOOM_STEPS.find((step) => step > now && apart(step)) ?? ZOOM_STEPS.at(-1)!;
  return [...ZOOM_STEPS].reverse().find((step) => step < now && apart(step)) ?? ZOOM_STEPS[0]!;
}

/** The view at a new zoom, the point at the panel's middle kept where it is; the fit has no pan. */
export function zoomed(view: CanvasView, zoom: CanvasZoom, fit: number): CanvasView {
  if (zoom === "fit") return { ...view, zoom, pan: { x: 0, y: 0 } };
  const by = stageScale(zoom, fit) / stageScale(view.zoom, fit);
  return { ...view, zoom, pan: { x: view.pan.x * by, y: view.pan.y * by } };
}

/**
 * The view moved by a pointer's travel, held so that the panel's middle stays on the frame: the
 * pan reaches at most half the frame's drawn size each way.
 */
export function panned(
  view: CanvasView,
  dx: number,
  dy: number,
  stage: { width: number; height: number },
  fit: number,
): CanvasView {
  const scale = stageScale(view.zoom, fit);
  const hold = (value: number, size: number) => Math.max(-(size * scale) / 2, Math.min((size * scale) / 2, value));
  return { ...view, pan: { x: hold(view.pan.x + dx, stage.width), y: hold(view.pan.y + dy, stage.height) } };
}
